import React from 'react';
import {Pressable, View} from 'native-base';
import {useTheme} from 'styled-components/native';
import Attribute from '../Attribute/Attribute';
import ContainedImage from 'components/ContainedImage';
import {
  ButtonType,
  CaesarsButton,
  SVG,
  useIsDesktop,
  useIsLargeDesktop,
} from '@eCommerce/frontend-legacy-shared-library-native';
import CRLogo from '../../../assets/caesars_rewards_dark.svg';
import {DateTime} from 'luxon';

export interface ReservationCardProps {
  propertyName: string;
  marketName: string;
  regionCode: string;
  checkInDate: string;
  checkOutDate: string;
  confirmationCode: string;
  onPress: () => void;
  testID?: string;
  imageUrl: string;
  adults: string;
  kids: string;
  checkInUrl?: string;
  detailsUrl?: string;
  showCheckIn: boolean;
  isCheckInDisabled?: boolean;
  checkInText?: string;
  showDetails: boolean;
  isHomeScreen: boolean;
}

const ReservationCard: React.FC<ReservationCardProps> = ({
  propertyName,
  marketName,
  regionCode,
  checkInDate,
  checkOutDate,
  confirmationCode,
  onPress,
  testID,
  imageUrl,
  adults,
  kids,
  checkInUrl,
  detailsUrl,
  showCheckIn,
  isCheckInDisabled,
  checkInText,
  showDetails,
  isHomeScreen,
}) => {
  const {colors} = useTheme() as any;
  const {white} = colors;
  const isDesktop = useIsDesktop();
  const isLargeDesktop = useIsLargeDesktop();

  const onCheckInPress = () => {
    window.open(checkInUrl, '_blank');
  };

  const onDetailsPress = () => {
    window.open(detailsUrl, '_blank');
  };

  const formattedCheckInDate = DateTime.fromFormat(
    checkInDate,
    'MM-dd-yyyy'
  ).toFormat('MMM dd, yyyy');

  const formattedCheckOutDate = DateTime.fromFormat(
    checkOutDate,
    'MM-dd-yyyy'
  ).toFormat('MMM dd, yyyy');

  const CheckInButton = () => {
    return (
      <View marginTop="10px">
        <CaesarsButton
          title={checkInText ?? ''}
          disabled={isCheckInDisabled}
          isFullWidth={!isDesktop}
          onPress={onCheckInPress}
          alignSelf="start"
          testID="check-in-button"
        />
      </View>
    );
  };

  const ViewDetailsButton = () => {
    return (
      <View flex={1} marginTop="10px">
        <CaesarsButton
          title="View Details"
          type={ButtonType.SECONDARY}
          isFullWidth={!isDesktop}
          onPress={onDetailsPress}
          testID="view-details-button"
          alignSelf="start"
        />
      </View>
    );
  };

  const PropertyImage = () => {
    return imageUrl ? (
      <ContainedImage
        imageUrl={imageUrl}
        style={{
          width: isDesktop ? 250 : '100%',
          height: isDesktop ? 150 : 200,
          marginBottom: isDesktop ? undefined : 16,
        }}
        alt={`${testID} resort exterior image`}
      />
    ) : (
      <View
        width={isDesktop ? 250 : '100%'}
        height={isDesktop ? 150 : 200}
        justifyContent="center"
        alignItems="center">
        <SVG
          style={{
            marginBottom: isDesktop ? undefined : 16,
            width: isDesktop ? 250 : 300,
          }}
          source={CRLogo}
          alt="Caesars-Rewards-logo"
        />
      </View>
    );
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="View Reservation Details"
      testID={`${testID}-reservation-card`}
      onPress={onPress}
      shadow={0}
      width="100%"
      minHeight={isDesktop ? '160px' : 'auto'}
      zIndex={1}
      backgroundColor={white}
      borderRadius={4}
      padding={isDesktop ? '24px' : '16px'}
      mb="16px"
      _hover={{shadow: 1}}>
      <View flexDirection={isDesktop ? 'row' : 'column'} width="100%">
        <View
          flexDirection={isDesktop ? 'row' : 'column'}
          flex={isDesktop ? 1 : undefined}
          width="100%">
          <PropertyImage />
          <View flex={1} paddingLeft={isDesktop ? '16px' : undefined}>
            {isHomeScreen && (isDesktop || isLargeDesktop) ? (
              <Attribute attribute="Property" value={propertyName} />
            ) : null}
            <View flexDirection={isDesktop ? 'row' : 'column'} flex={1}>
              <View width={!isDesktop ? '100%' : 'auto'} flex={1}>
                <View flexDirection={isDesktop ? 'column' : 'row'} width="100%">
                  <View flex={1}>
                    {isHomeScreen && isDesktop ? null : (
                      <Attribute attribute="Property" value={propertyName} />
                    )}
                    <View flexDirection={isDesktop ? undefined : 'row'}>
                      <View flex={1}>
                        <Attribute attribute="Location" value={marketName} />
                      </View>
                      {!isDesktop ? (
                        <View flex={1}>
                          <Attribute
                            attribute="Confirmation"
                            value={confirmationCode}
                          />
                        </View>
                      ) : null}
                    </View>
                    {isHomeScreen && isDesktop ? (
                      <>
                        <Attribute
                          attribute="Check-In"
                          value={formattedCheckInDate}
                        />
                        <Attribute
                          attribute="Checkout"
                          value={formattedCheckOutDate}
                        />
                      </>
                    ) : null}
                  </View>
                  {/**
                   * Button shown for both Stays and Home screens, on all breakpoints except isDesktop on HomeScreen.
                   */}
                  {isDesktop && showCheckIn ? (
                    isHomeScreen ? (
                      isLargeDesktop ? (
                        <CheckInButton />
                      ) : null
                    ) : (
                      <CheckInButton />
                    )
                  ) : null}
                </View>
              </View>
              {/* Below View implemented everywhere but the 2 desktop breakpoints on HomeScreen */}
              <View
                flexDirection="row"
                width="100%"
                marginBottom={isDesktop ? undefined : '24px'}
                flex={1}>
                {isHomeScreen && isDesktop ? null : (
                  <View flex={1}>
                    <Attribute
                      attribute="Check-In"
                      value={formattedCheckInDate}
                    />
                    <Attribute
                      attribute="Checkout"
                      value={formattedCheckOutDate}
                    />
                    {isDesktop ? (
                      <Attribute
                        attribute="Confirmation"
                        value={confirmationCode}
                      />
                    ) : null}
                  </View>
                )}
                <View flex={1}>
                  {isHomeScreen && isDesktop ? (
                    <Attribute
                      attribute="Confirmation"
                      value={confirmationCode}
                    />
                  ) : null}
                  <Attribute attribute="Adults" value={adults} />
                  <Attribute attribute="Children" value={kids} />
                  {/**
                   * Button shown for both Stays and Home screens, on all breakpoints except isDesktop on HomeScreen.
                   */}
                  {isDesktop && showDetails ? (
                    isHomeScreen ? (
                      isLargeDesktop ? (
                        <ViewDetailsButton />
                      ) : null
                    ) : (
                      <ViewDetailsButton />
                    )
                  ) : null}
                </View>
              </View>
            </View>
            {isHomeScreen && isDesktop && !isLargeDesktop ? (
              <>
                {showCheckIn ? (
                  <CaesarsButton
                    title={checkInText ?? ''}
                    disabled={isCheckInDisabled}
                    isFullWidth
                    marginBottom="12px"
                    onPress={onCheckInPress}
                    testID="check-in-button"
                  />
                ) : null}
                {showDetails ? (
                  <CaesarsButton
                    title="View Details"
                    type={ButtonType.SECONDARY}
                    isFullWidth
                    onPress={onDetailsPress}
                    testID="view-details-button"
                  />
                ) : null}
              </>
            ) : null}
          </View>
        </View>
        {isDesktop ? null : (
          <View>
            {showCheckIn && (
              <CaesarsButton
                title={checkInText ?? ''}
                disabled={isCheckInDisabled}
                isFullWidth
                marginBottom="12px"
                onPress={onCheckInPress}
                testID="check-in-button"
              />
            )}
            {showDetails && (
              <CaesarsButton
                title="View Details"
                type={ButtonType.SECONDARY}
                isFullWidth
                onPress={onDetailsPress}
                testID="view-details-button"
              />
            )}
          </View>
        )}
      </View>
    </Pressable>
  );
};

export default ReservationCard;
